import axios, { isRequestCanceled } from '@/plugins/axios';

// ─── Request handling ─────────────────────────────────────────────────────────

const handleRequest = async (request, config = {}) => {
  if (config.noCancelRequest) {
    return request();
  }

  try {
    return await request();
  } catch (error) {
    if (isRequestCanceled(error)) {
      return null;
    }

    throw error;
  }
};

// ─── Daily endpoints ──────────────────────────────────────────────────────────

export const getSalesByArea = async (user, channel, quarter, year) =>
  handleRequest(async () => {
    const { data } = await axios.get('/daily/sales-by-area', {
      params: { user, channel, quarter, year },
    });
    return data.items;
  });

export const getBackorders = async (user, channel, period) =>
  handleRequest(async () => {
    const { data } = await axios.get('/daily/backorders', {
      params: { user, channel, period },
    });
    return data;
  });

export const getCumulativeGrowth = async (user, channel, period) =>
  handleRequest(async () => {
    const { data } = await axios.get('/daily/cumulative-growth', {
      params: { user, channel, period },
    });
    return data;
  });

export const getBillingVsEstimate = async (user, channel, period = undefined) =>
  handleRequest(async () => {
    const { data } = await axios.get('/daily/billing-vs-estimate', {
      params: { user, channel, ...(period !== undefined && { period }) },
    });
    return data;
  });

export const getOrders = async (user, channel) =>
  handleRequest(async () => {
    const { data } = await axios.get('/daily/orders', {
      params: { user, channel },
    });
    return data;
  });

export const getIelPaymentResume = async (user, channel) =>
  handleRequest(async () => {
    const { data } = await axios.get('daily/payment/iel-resume', {
      params: { user, channel },
    });
    return data;
  });

export const getIelPaymentsDetail = async (user, channel) =>
  handleRequest(async () => {
    const { data } = await axios.get('daily/iel-payments-detail', {
      params: { user, channel },
    });
    return data;
  });

export const getUsersSummary = async (user, channel, period, option) =>
  handleRequest(async () => {
    const { data } = await axios.get('/daily/users-summary', {
      params: { user, channel, period, option },
      cachable: true,
    });
    return data.data;
  });

export const getCustomersSummary = async (user, channel) => {
  const { data } = await axios.get('/daily/seller/customers-summary', {
    params: { user, channel },
    noCancelRequest: true,
    cachable: true,
  });
  return data;
};

export const getResumeSummary = async (user, channel) => {
  const { data } = await axios.get('daily/resume-summary', {
    params: { user, channel },
    noCancelRequest: true,
    cachable: true,
  });
  return data;
};

export const getCustomersViaIelMonthly = async (user, channel, period) =>
  handleRequest(async () => {
    const { data } = await axios.get('/daily/customers-via-iel-monthly', {
      params: { user, channel, period },
    });
    return data;
  });

export const getPncProducts = async (user, channel) =>
  handleRequest(async () => {
    const { data } = await axios.get('/daily/pnc-products', {
      params: { channel, user },
    });
    return data;
  });

export const getGenNProducts = async (user, channel) =>
  handleRequest(async () => {
    const { data } = await axios.get('/daily/gen-n-products', {
      params: { channel, user },
    });
    return data;
  });

export const getCustomersViaIel = async (user, channel, period) =>
  handleRequest(async () => {
    const { data } = await axios.get('/daily/customers-via-iel', {
      params: { channel, user, period },
    });
    return data;
  });

export const getDailyCredit = async (user, channel) =>
  handleRequest(async () => {
    const { data } = await axios.get('/daily/credit', {
      params: { user, channel },
    });
    return data;
  });

export const getGeneralInfo = async (user, channel) =>
  handleRequest(async () => {
    const { data } = await axios.get('daily/general-user-data', {
      params: { user, channel },
    });
    return data;
  });

// ─── Seller endpoints ─────────────────────────────────────────────────────────

export const getHistoricoVeppfd = async (
  employeeNumber,
  channel,
  month,
  year
) => {
  const { data } = await axios.get('/seller/home/historico-veppfd', {
    params: {
      employee_number: Number(employeeNumber),
      P_VTWEG: channel,
      number_month: month,
      number_year: year,
    },
    noCancelRequest: true,
    cachable: true,
  });
  return data.data;
};

export const getDiaHabil = async (
  employeeNumber,
  channel,
  trim,
  month,
  year,
  opt
) => {
  const { data } = await axios.get('/seller/home/dia-habil', {
    params: {
      employee_number: Number(employeeNumber),
      P_VTWEG: channel,
      trim,
      month,
      year,
      opt,
    },
    noCancelRequest: true,
    cachable: true,
  });
  return data.data;
};

export const getVbUsuarios = async (
  employeeNumber,
  type,
  trim,
  month,
  year
) => {
  const { data } = await axios.get('/seller/home/vbUsuarios', {
    params: {
      employee_number: Number(employeeNumber),
      type,
      trim,
      month,
      year,
    },
    noCancelRequest: true,
    cachable: true,
  });
  return data.data;
};

export const getSellerBilling = async (sellerId, month, year, channel) => {
  const { data } = await axios.post(
    '/seller/home/billing',
    { seller_id: sellerId, month, year, channel },
    { noCancelRequest: true, cachable: true }
  );
  return data;
};

export const getSalesByDiv = async (
  sellerId,
  trim,
  year,
  channel,
  cancelToken = undefined
) =>
  handleRequest(async () => {
    const { data } = await axios.post(
      '/seller/home/sales-by-div',
      { seller_id: sellerId, trim, year, channel },
      { ...(cancelToken && { cancelToken }) }
    );
    return data;
  });

export const getSellerOrders = async (params, axiosConfig = {}) =>
  handleRequest(async () => {
    const { data } = await axios.post(
      '/seller/home/orders',
      params,
      axiosConfig
    );
    return data;
  }, axiosConfig);

export const getNetSales = async (params, axiosConfig = {}) =>
  handleRequest(async () => {
    const { data } = await axios.post(
      '/seller/home/net-sales',
      params,
      axiosConfig
    );
    return data;
  }, axiosConfig);

export const getSalesCommissions = async (params) =>
  handleRequest(async () => {
    const { data } = await axios.post('/seller/home/sales-commissions', params);
    return data;
  });

export const getSellerResume = async (params, axiosConfig = {}) =>
  handleRequest(async () => {
    const { data } = await axios.post(
      '/seller/home/resume',
      params,
      axiosConfig
    );
    return data;
  }, axiosConfig);

// ─── End of repository ────────────────────────────────────────────────────────
